/**
 * Scoped export for the group menu's **Export to Excel** and the multi-select bar's
 * **Export** (`specs/M1-B10-shell-chrome.md` §4.6, §4.7).
 *
 * **Registered deviation.** monday exports the scope as `.xlsx`; ours writes `.csv`. B9's
 * `export.board` takes a board and nothing else — there is no `export.items` /
 * `export.group` procedure, and adding one is a service change this round does not own. A CSV
 * of exactly the right rows opens in Excel and is a truer answer than either a greyed entry or
 * a whole-board XLSX, so it ships with the deviation written down rather than hidden.
 *
 * The values come from the rows already on screen (`ColumnValue.text`), which is what monday's
 * own export column contains for every type.
 */
import type { BoardColumn, ItemRow } from '../../data/types';

/** RFC 4180: quote when the value holds a comma, a quote or a newline; double an inner quote. */
export function csvCell(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

/**
 * `ItemRow.values` is `Record<string, unknown>` by design (the registry owns each shape), so
 * the export reads the one field every column value carries — `text`, monday's own display
 * string — and falls back to the empty string rather than to `[object Object]`.
 */
export function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object' && 'text' in (value as Record<string, unknown>)) {
    const text = (value as { text?: unknown }).text;
    return typeof text === 'string' ? text : '';
  }
  return '';
}

export function toCsv(
  columns: readonly BoardColumn[],
  rows: readonly ItemRow[],
  nameHeader = 'Name',
): string {
  const header = [nameHeader, ...columns.map((column) => column.title)];
  const body = rows.map((row) => [
    row.name,
    ...columns.map((column) => cellText(row.values[String(column.id)])),
  ]);
  return [header, ...body].map((line) => line.map(csvCell).join(',')).join('\r\n');
}

/** A filename Excel and every OS accept: no slash, no colon, no trailing dot. */
export function safeFilename(...parts: string[]): string {
  const base = parts
    .filter(Boolean)
    .join(' - ')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\.+$/, '');
  return `${base.slice(0, 120) || 'export'}.csv`;
}

/**
 * Hand the CSV to the browser. A BOM is prepended so Excel opens a UTF-8 file as UTF-8 rather
 * than as the legacy code page — without it every accented name in this account is mojibake.
 */
export function downloadCsv(filename: string, csv: string): void {
  // U+FEFF, written as an escape: a literal BOM in the source is an irregular-whitespace error.
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoking synchronously cancels the download in Safari; one tick is enough everywhere.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
